import { useTranslation } from 'react-i18next';
import { FieldData, Select, SubPartnerAccountStatus, TextField, useForm } from 'vopay';
import { Checkbox } from 'UI';
import { usePartnerAccountStatusLabel } from '@/hooks';

/** Field names double as the inputs' `name`s (Cypress selects on `select-Status-trigger` / `textField-SearchText`). */
export type PartnerFilterValues = {
  /** Empty for no status filter. */
  Status: SubPartnerAccountStatus | '';
  SearchText: string;
  /** The chart view's checkbox (ID-14095): a field only for its place in the row; its value is the host's, never Formik's. */
  ShowDirectAccounts: false;
};

/** The chart view's own filter state (ID-14095): the Show Direct Accounts checkbox. Absent in table view. */
export type PartnerChartViewFilter = {
  isShowingDirectAccounts: boolean;
  onShowDirectAccountsChange: (isOn: boolean) => void;
};

type UsePartnerFilterFormConfigProps = {
  /** Comes from the URL, so the form re-seeds whenever the query string changes. */
  initialValues: Omit<PartnerFilterValues, 'ShowDirectAccounts'>;
  /** The statuses the Status filter offers, in the order they should appear. */
  statuses: SubPartnerAccountStatus[];
  /** Disables the Status filter while its options are still being fetched. */
  isLoadingStatuses: boolean;
  chartView?: PartnerChartViewFilter;
  onSubmit: (values: PartnerFilterValues) => void;
};

export default function usePartnerFilterFormConfig({
  initialValues,
  statuses,
  isLoadingStatuses,
  chartView,
  onSubmit,
}: UsePartnerFilterFormConfigProps) {
  const config = useConfig(statuses, isLoadingStatuses, chartView);

  const { ctx, renderFormElements } = useForm<PartnerFilterValues>({
    // A constant, not the checkbox's state: the form re-seeds whenever these change, which would
    // throw away a keyword typed but not yet searched for every time the box is ticked.
    initialValues: { ...initialValues, ShowDirectAccounts: false },
    config,
    onSubmit,
    enableReinitialize: true,
  });

  return { ctx, renderFormElements: () => renderFormElements({ config }) };
}

function useConfig(
  statuses: SubPartnerAccountStatus[],
  isLoadingStatuses: boolean,
  chartView: PartnerChartViewFilter | undefined,
): FieldData<PartnerFilterValues>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.SearchForm' });
  const { resolveStatusLabel } = usePartnerAccountStatusLabel();

  // The raw status stays the option value (it is what the request filters on); only the label is
  // localized. No "All" option: a blank, clearable Select is the no-filter state.
  const statusOptions = statuses.map((status) => ({ label: resolveStatusLabel(status), value: status }));

  return [
    {
      fieldName: 'Status',
      getInputState: () => (isLoadingStatuses ? 'disabled' : 'optional'),
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <Select
          version="2"
          showClear
          {...getFormikData<PartnerFilterValues['Status']>({})}
          placeholder={t('statusPlaceholder', { defaultValue: 'Status' })}
          options={statusOptions}
          sort={false}
        />
      ),
    },
    {
      fieldName: 'ShowDirectAccounts',
      getInputState: () => 'optional',
      showField: () => chartView !== undefined,
      // Figma `Check option` (the design-system `Selectors` box, then its label), in the filter row.
      // Ticking it redraws the chart at once, so the value is the host's rather than Formik's.
      renderField: () => (
        <Checkbox
          name="ShowDirectAccounts"
          label={t('showDirectAccounts', { defaultValue: 'Show Direct Accounts' })}
          checked={chartView?.isShowingDirectAccounts ?? false}
          onChange={(isOn) => chartView?.onShowDirectAccountsChange(isOn)}
        />
      ),
    },
    {
      fieldName: 'SearchText',
      getInputState: () => 'optional',
      showField: () => true,
      renderField: ({ getFormikData }) => (
        <TextField
          {...getFormikData<PartnerFilterValues['SearchText']>({})}
          placeholder={t('keywordPlaceholder', { defaultValue: 'Keyword' })}
          variant="outlineShadow"
        />
      ),
    },
  ];
}
